import React, { useEffect, useState, useRef } from "react";
import { Link } from "react-router-dom";
import { Bell, AlertTriangle, CheckCircle2, Info } from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../lib/auth";
import { useI18n } from "../lib/i18n";

const sevIcon = (s) => {
  if (s === "critical") return <AlertTriangle size={14} className="text-[#881a27]" />;
  if (s === "warning") return <AlertTriangle size={14} className="text-[#92400e]" />;
  return <Info size={14} className="text-[#475569]" />;
};

const sevBg = (s) => {
  if (s === "critical") return "bg-[#ffefef] border-l-2 border-[#881a27]";
  if (s === "warning") return "bg-[#fef3c7] border-l-2 border-[#92400e]";
  return "bg-white border-l-2 border-[#475569]";
};

export default function AlertsBell() {
  const { user } = useAuth();
  const { t } = useI18n();
  const [data, setData] = useState({ alerts: [], count: 0, critical_count: 0 });
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  const load = async () => {
    try {
      const r = await api.get("/alerts/my");
      setData(r.data);
    } catch { /* non-fatal — alerts polling */ }
  };
  useEffect(() => {
    if (!user) return;
    load();
    const id = setInterval(load, 60000);
    return () => clearInterval(id);
  }, [user]); // eslint-disable-line

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  if (!user) return null;

  return (
    <div ref={ref} className="relative">
      <button
        data-testid="alerts-bell-btn"
        onClick={() => setOpen(!open)}
        className="relative w-10 h-10 rounded-[12px] bg-white border border-[#e2e8f0] hover:border-[#475569] flex items-center justify-center transition-all shrink-0"
        aria-label="Alerts"
      >
        <Bell size={18} className="text-[#475569]" />
        {data.count > 0 && (
          <span data-testid="alerts-bell-badge" className={`absolute -top-1 -right-1 min-w-[18px] h-[18px] rounded-full text-[10px] font-bold text-white px-1 flex items-center justify-center ring-2 ring-white ${data.critical_count > 0 ? "bg-[#881a27]" : "bg-[#475569]"}`}>
            {data.count > 99 ? "99+" : data.count}
          </span>
        )}
      </button>

      {open && (
        <div data-testid="alerts-dropdown" className="absolute right-0 mt-2 w-[min(400px,calc(100vw-2rem))] max-h-[520px] bg-white rounded-[16px] shadow-2xl border border-[#e2e8f0] overflow-hidden z-50">
          <div className="p-4 border-b border-[#eeeded] flex items-center justify-between">
            <div>
              <div className="font-semibold text-[15px] text-[#323232]">{t("dash.smart_alerts")}</div>
              <div className="text-[11px] text-[#747474]">{data.count} · {data.critical_count}</div>
            </div>
            <button onClick={load} className="text-[11px] text-[#475569] hover:underline">{t("common.refresh")}</button>
          </div>
          <div className="max-h-[440px] overflow-y-auto">
            {data.alerts.length === 0 ? (
              <div className="p-8 text-center text-[13px] text-[#747474]">
                <CheckCircle2 size={28} className="mx-auto mb-2 text-[#008932]" />
                {t("approvals.empty")}
              </div>
            ) : (
              data.alerts.slice(0, 20).map((a) => (
                <Link
                  key={a.id}
                  to={a.link}
                  onClick={() => setOpen(false)}
                  className={`block p-3 hover:bg-[#f6f6f6] transition-all ${sevBg(a.severity)}`}
                  data-testid={`alert-item-${a.type}`}
                >
                  <div className="flex items-start gap-2">
                    {sevIcon(a.severity)}
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-[13px] text-[#323232]">{a.title_key ? t(a.title_key) : a.title}</div>
                      <div className="text-[11px] text-[#747474] mt-0.5">{a.description}</div>
                    </div>
                  </div>
                </Link>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
